import React from "react";
import { DragControls } from "motion/react";
import { CloseIcon, ClearHistoryIcon, SettingsIcon, SamplesIcon } from "./icons";
import useConfig from "../../../../../demos/hooks/useConfig";

// Pop Out Icon Component
const PopOutIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

// Pin Icon Component
const PinIcon: React.FC<{ isPinned: boolean }> = ({ isPinned }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill={isPinned ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={isPinned ? "rotate-45" : ""}
  >
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
  </svg>
);

interface DrawerHeaderProps {
  onClose: () => void;
  openConfirmModal: () => void;
  openFieldConfigModal: () => void;
  dragControls: DragControls;
  isPinned?: boolean;
  onTogglePin?: () => void;
  showSamples?: boolean;
  onToggleSamples?: () => void;
  isPoppedOut?: boolean;
  onTogglePopOut?: () => void;
}

const DrawerHeader: React.FC<DrawerHeaderProps> = ({
  onClose,
  openConfirmModal,
  openFieldConfigModal,
  dragControls,
  isPinned = false,
  onTogglePin,
  showSamples = false,
  onToggleSamples,
  isPoppedOut = false,
  onTogglePopOut,
}) => {
  const { config } = useConfig();
  return (
    <div
      className={`flex items-center justify-between p-4 select-none ${
        isPinned ? "cursor-default" : "cursor-move"
      }`}
      style={{ userSelect: "none" }}
      onPointerDown={(e) => {
        if (!isPinned) {
          dragControls.start(e);
        }
      }}
    >
      <div className="flex items-center space-x-2 justify-between w-full">
        <div className="flex items-center space-x-3">
          <img src="/CADViewer_Submark.svg" alt="CADViewer Logo" className="w-8 h-8" />
          <h2 className="text-lg font-bold" style={{ color: '#0A0042' }}>
            CADViewer AI Assistant
          </h2>
        </div>
        <div 
          className="flex items-center space-x-2 justify-end"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* Pop Out button */}
          {onTogglePopOut && (
            <button
              onClick={onTogglePopOut}
              className={`ml-2 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-md p-1 flex items-center transition-colors ${
                isPoppedOut
                  ? "text-orange-500 hover:text-orange-600"
                  : "text-gray-500 hover:text-orange-500"
              }`}
              aria-label={isPoppedOut ? "Return chat to main window" : "Pop out chat"}
              title={isPoppedOut ? "Return to main window" : "Pop out into separate window"}
            >
              <PopOutIcon />
            </button>
          )}
          {/* Pin/Unpin button */}
          {onTogglePin && (
            <button
              onClick={onTogglePin}
              className={`ml-2 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-md p-1 flex items-center transition-colors ${
                isPinned
                  ? "text-orange-500 hover:text-orange-600"
                  : "text-gray-500 hover:text-orange-500"
              }`}
              aria-label={isPinned ? "Unpin chat" : "Pin chat to side"}
              title={isPinned ? "Unpin chat (floating mode)" : "Pin chat to the right side"}
            >
              <PinIcon isPinned={isPinned} />
            </button>
          )}
          {/* Sample questions button */}
          {onToggleSamples && (
            <button
              onClick={onToggleSamples}
              className={`ml-2 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-md p-1 flex items-center transition-colors ${
                showSamples
                  ? "text-orange-500 hover:text-orange-600"
                  : "text-gray-500 hover:text-orange-500"
              }`}
              aria-label={showSamples ? "Hide sample questions" : "Show sample questions"}
              title={showSamples ? "Hide sample questions" : "Show sample questions"}
            >
              <SamplesIcon />
            </button>
          )}
          {config.enableAIFieldTypes && (
            <button
              onClick={openFieldConfigModal}
              className="ml-2 text-gray-500 hover:text-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-md p-1 flex items-center"
              aria-label="Field settings"
              title="Configure field types"
            >
              <SettingsIcon />
            </button>
          )}
          <button
            onClick={openConfirmModal}
            className="ml-2 text-gray-500 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-md p-1 flex items-center"
            aria-label="Clear history"
            title="Clear message history"
          >
            <ClearHistoryIcon />
          </button>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-md p-1"
            aria-label="Close chat"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DrawerHeader;
